import type { AdminLearningContentSubCategory } from '../types/domain';

export type LearningContentTemplateScope = 'admin' | 'team' | 'user';

export type LearningContentTemplateSelection = {
  scope: LearningContentTemplateScope;
  name: string;
};

export const EMPTY_LEARNING_CONTENT_TEMPLATE_SELECTION: LearningContentTemplateSelection = {
  scope: 'admin',
  name: '',
};

export function programTermNumberToLearningSubCategory(
  termNumber: 1 | 2 | null | undefined
): AdminLearningContentSubCategory {
  return termNumber === 2 ? 'term2' : 'term1';
}

function foldLearningContentTemplateName(raw: string): string {
  return raw
    .normalize('NFC')
    .trim()
    .replace(/\s+/g, '')
    .replace(/[()[\]{}·・‧.,\-/]/g, '')
    .toLocaleLowerCase('ko-KR');
}

function stemLearningContentTemplateName(raw: string): string {
  const folded = foldLearningContentTemplateName(raw);
  const stripped = folded.replace(/(교과|과)$/u, '');
  return stripped.length >= 2 ? stripped : folded;
}

/**
 * 프로그램에 등록한 편제명과 학습내용 편제명이 달라도 같은 과목으로 고른다.
 * 공백·NFC/NFD·말미 「과/교과」·포함 관계를 본다.
 */
export function findPreferredLearningContentTemplateName(
  preferredName: string,
  names: string[]
): string | null {
  const wanted = preferredName.trim();
  if (!wanted || names.length === 0) return null;
  const exact = names.find((name) => name === wanted);
  if (exact) return exact;

  const foldedWanted = foldLearningContentTemplateName(wanted);
  if (foldedWanted) {
    const foldedHit = names.find((name) => foldLearningContentTemplateName(name) === foldedWanted);
    if (foldedHit) return foldedHit;
  }

  const stemWanted = stemLearningContentTemplateName(wanted);
  if (stemWanted) {
    const stemHits = names.filter((name) => stemLearningContentTemplateName(name) === stemWanted);
    if (stemHits.length === 1) return stemHits[0];
    if (stemHits.length > 1) {
      return [...stemHits].sort(
        (a, b) => a.length - b.length || a.localeCompare(b, 'ko')
      )[0];
    }
  }

  if (stemWanted.length < 2) return null;
  const contained = names.filter((name) => {
    const stem = stemLearningContentTemplateName(name);
    if (stem.length < 2) return false;
    return stem.includes(stemWanted) || stemWanted.includes(stem);
  });
  if (contained.length === 1) return contained[0];
  if (contained.length > 1) {
    return [...contained].sort((a, b) => {
      const da = Math.abs(stemLearningContentTemplateName(a).length - stemWanted.length);
      const db = Math.abs(stemLearningContentTemplateName(b).length - stemWanted.length);
      return da - db || a.length - b.length || a.localeCompare(b, 'ko');
    })[0];
  }
  return null;
}

function selectionFromPreferredName(
  preferredName: string,
  adminNames: string[],
  personalNames: string[],
  teamNames: string[]
): LearningContentTemplateSelection | null {
  const adminHit = findPreferredLearningContentTemplateName(preferredName, adminNames);
  if (adminHit) return { scope: 'admin', name: adminHit };
  const personalHit = findPreferredLearningContentTemplateName(preferredName, personalNames);
  if (personalHit) return { scope: 'user', name: personalHit };
  const teamHit = findPreferredLearningContentTemplateName(preferredName, teamNames);
  if (teamHit) return { scope: 'team', name: teamHit };
  return null;
}

/** 학년·학기 변경 등 목록 갱신 후에도 scope+name 선택을 유지한다. */
export function resolveLearningContentTemplateSelection(
  prev: LearningContentTemplateSelection,
  adminNames: string[],
  personalNames: string[],
  teamNames: string[] = [],
  preferredName = ''
): LearningContentTemplateSelection {
  const { scope, name } = prev;
  if (name) {
    if (scope === 'admin' && adminNames.includes(name)) return prev;
    if (scope === 'user' && personalNames.includes(name)) return prev;
    if (scope === 'team' && teamNames.includes(name)) return prev;
    if (adminNames.includes(name)) return { scope: 'admin', name };
    if (personalNames.includes(name)) return { scope: 'user', name };
    if (teamNames.includes(name)) return { scope: 'team', name };
    const prevHit = selectionFromPreferredName(name, adminNames, personalNames, teamNames);
    if (prevHit) return prevHit;
  }
  const preferredHit = selectionFromPreferredName(preferredName, adminNames, personalNames, teamNames);
  if (preferredHit) return preferredHit;
  if (adminNames[0]) return { scope: 'admin', name: adminNames[0] };
  if (personalNames[0]) return { scope: 'user', name: personalNames[0] };
  if (teamNames[0]) return { scope: 'team', name: teamNames[0] };
  return EMPTY_LEARNING_CONTENT_TEMPLATE_SELECTION;
}
